import {
  BrowserCollectionCoordinator,
  createBrowserWASQLitePersistence,
  openBrowserWASQLiteOPFSDatabase,
  type PersistedCollectionPersistence,
} from '@tanstack/browser-db-sqlite-persistence'
import { PersistenceUnavailableError } from '@tanstack/db-sqlite-persistence-core'

const DATABASE_PREFIX = 'cargoking-db-'

export type DbSession = {
  close: () => Promise<void>
  // null when OPFS is unavailable (private browsing, older browsers): collections stay in memory
  persistence: null | PersistedCollectionPersistence
}

let current: undefined | { session: Promise<DbSession>; userId: number }
// Opening, closing and deleting touch the same OPFS files, so they run one at a time
let queue: Promise<unknown> = Promise.resolve()

const enqueue = <T>(task: () => Promise<T>) => {
  const result = queue.then(task, task)
  queue = result
  return result
}

const getDatabaseName = (userId: number) => `${DATABASE_PREFIX}${userId}`

const getOpfsRoot = async () => {
  try {
    return await navigator.storage.getDirectory()
  } catch (error) {
    // Storage is blocked (private browsing), so there is nothing on disk to manage
    if (error instanceof DOMException && error.name === 'SecurityError') return null

    throw error
  }
}

// OPFS on this origin is used by nothing else. Besides the databases it collects the temp-file
// directories wa-sqlite leaves behind on every open, so everything outside `keep` is removed
const deleteDatabases = async (keep?: string) => {
  const root = await getOpfsRoot()

  if (!root) return

  for await (const name of root.keys()) {
    if (keep && name.startsWith(keep)) continue

    try {
      await root.removeEntry(name, { recursive: true })
    } catch (error) {
      // Another tab still holds the entry open; it is removed when that tab's session ends
      if (!(error instanceof DOMException && error.name === 'NoModificationAllowedError')) {
        throw error
      }
    }
  }
}

const openDbSession = async (userId: number): Promise<DbSession> => {
  const databaseName = getDatabaseName(userId)

  try {
    // The separator keeps user 1 from matching the files of user 12
    await deleteDatabases(`${databaseName}.`)

    const database = await openBrowserWASQLiteOPFSDatabase({
      databaseName: `${databaseName}.sqlite`,
    })
    const coordinator = new BrowserCollectionCoordinator({ dbName: databaseName })

    return {
      close: async () => {
        coordinator.dispose()
        await database.close?.()
      },
      persistence: createBrowserWASQLitePersistence({ coordinator, database }),
    }
  } catch (error) {
    // The database only speeds things up, so it must never take a feature down with it. Missing
    // OPFS is expected (private browsing); anything else is reported and also runs in memory
    if (!(error instanceof PersistenceUnavailableError)) reportError(error)

    return { close: async () => {}, persistence: null }
  }
}

const closeCurrent = () => {
  const previous = current?.session
  current = undefined

  return async () => {
    // A session that failed to open has nothing to close
    const [opened] = await Promise.allSettled([previous])

    if (opened.status === 'fulfilled') await opened.value?.close()
  }
}

/**
 * One SQLite database per user. Switching user closes the previous database and deletes every
 * database that belongs to somebody else, so cached data never crosses accounts.
 */
export const getDbSession = (userId: number) => {
  if (current?.userId !== userId) {
    const closePrevious = closeCurrent()

    current = {
      session: enqueue(async () => {
        await closePrevious()
        return openDbSession(userId)
      }),
      userId,
    }
  }

  return current.session
}

export const wipeDbSessions = () => {
  const closePrevious = closeCurrent()

  return enqueue(async () => {
    await closePrevious()
    await deleteDatabases()
  })
}
